import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Info, Plus, Save, Send, Trash2 } from "lucide-react";
import { api, errMsg, inr } from "../lib/api";
import { useCompany } from "../context/CompanyContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Textarea } from "../components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "../components/ui/tooltip";
import AddressFields, { EMPTY_ADDRESS, addressFromParty, addressText } from "../components/AddressFields";

const newLine = () => ({ item_id: null, name: "", hsn: "", unit: "Nos", qty: 1, rate: 0, discount_pct: 0, tax_rate: 18 });

const Tip = ({ text }) => (
  <TooltipProvider><Tooltip>
    <TooltipTrigger asChild><Info className="ml-1 inline h-3.5 w-3.5 text-slate-400 cursor-help" /></TooltipTrigger>
    <TooltipContent className="max-w-xs text-xs bg-slate-900 text-white">{text}</TooltipContent>
  </Tooltip></TooltipProvider>
);

export default function InvoiceForm() {
  const { activeId, active } = useCompany();
  const [params] = useSearchParams();
  const nav = useNavigate();
  const [parties, setParties] = useState([]);
  const [items, setItems] = useState([]);
  const [partyId, setPartyId] = useState(params.get("party") || "");
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState("");
  const [placeOfSupply, setPlaceOfSupply] = useState("");
  const [billingAddress, setBillingAddress] = useState(EMPTY_ADDRESS);
  const [shippingAddress, setShippingAddress] = useState(EMPTY_ADDRESS);
  const [invoiceType, setInvoiceType] = useState("composite");
  const [lines, setLines] = useState([newLine()]);
  const [charges, setCharges] = useState(0);
  const [received, setReceived] = useState(0);
  const [payMode, setPayMode] = useState("cash");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!activeId) return;
    api.get("/parties?limit=200").then((r) => setParties(r.data.items.filter((p) => !["supplier", "vendor"].includes(p.type)))).catch(() => {});
    api.get("/items?limit=200").then((r) => setItems(r.data.items)).catch(() => {});
  }, [activeId]);

  useEffect(() => {
    const p = parties.find((x) => x.id === partyId);
    if (p) {
      setPlaceOfSupply((v) => v || p.state || "");
      setBillingAddress(addressFromParty(p, "billing"));
      setShippingAddress(addressFromParty(p, "shipping").line1 ? addressFromParty(p, "shipping") : addressFromParty(p, "billing"));
    }
  }, [partyId, parties]);

  const intra = useMemo(() => {
    const supply = (placeOfSupply || "").trim().toLowerCase();
    const comp = (active?.state || "").trim().toLowerCase();
    return supply && comp ? supply === comp : true;
  }, [placeOfSupply, active]);

  const setLine = (i, k, v) => setLines((ls) => ls.map((l, j) => (j === i ? { ...l, [k]: v } : l)));
  const pickItem = (i, itemId) => {
    if (itemId === "custom") { setLine(i, "item_id", null); return; }
    const it = items.find((x) => x.id === itemId);
    if (it) setLines((ls) => ls.map((l, j) => j === i ? { ...l, item_id: it.id, name: it.name, hsn: it.hsn, unit: it.unit, rate: it.sale_price, tax_rate: it.tax_rate } : l));
  };

  const calc = useMemo(() => {
    let subtotal = 0, disc = 0, taxable = 0, cgst = 0, sgst = 0, igst = 0;
    for (const l of lines) {
      const base = (+l.qty || 0) * (+l.rate || 0);
      const d = base * (+l.discount_pct || 0) / 100;
      const t = base - d;
      const tax = t * (+l.tax_rate || 0) / 100;
      subtotal += base; disc += d; taxable += t;
      if (intra) { cgst += tax / 2; sgst += tax / 2; } else igst += tax;
    }
    const grand = taxable + cgst + sgst + igst + (+charges || 0);
    const total = Math.round(grand * 100) / 100;
    const rounded = Math.round(grand);
    return { subtotal, disc, taxable, cgst, sgst, igst, grand: total, roundOff: rounded - total, total: rounded };
  }, [lines, charges, intra]);

  const save = async (saveAs) => {
    if (!partyId) { toast.error("Select a customer"); return; }
    const validLines = lines.filter((l) => l.name.trim() && +l.qty > 0);
    if (validLines.length === 0) { toast.error("Add at least one item line"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/invoices", {
        party_id: partyId, date, due_date: dueDate || null, place_of_supply: placeOfSupply,
        billing_address_details: billingAddress, billing_address: addressText(billingAddress),
        shipping_address_details: shippingAddress, shipping_address: addressText(shippingAddress),
        invoice_type: invoiceType, items: validLines.map((l) => ({ ...l, qty: +l.qty, rate: +l.rate, discount_pct: +l.discount_pct, tax_rate: +l.tax_rate })),
        additional_charges: +charges || 0, notes, save_as: saveAs,
        amount_received: saveAs === "posted" ? +received || 0 : 0, payment_mode: payMode,
      });
      if (!data || !data.id) {
        throw new Error("Invoice was saved but the server did not return a valid invoice ID. Please refresh the invoice list and verify the document before trying again.");
      }
      toast.success(saveAs === "posted" ? `Invoice ${data.invoice_no || data.doc_no || data.number || "created"} posted` : "Draft saved");
      nav(`/invoices/${data.id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  const selectedParty = parties.find((p) => p.id === partyId);

  return (
    <div data-testid="invoice-form-page" className="max-w-5xl">
      <Button variant="ghost" size="sm" className="mb-3 -ml-2" onClick={() => nav("/invoices")} data-testid="back-to-invoices">
        <ArrowLeft className="mr-1 h-4 w-4" /> Invoices
      </Button>
      <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-sm p-5 sm:p-6 space-y-6">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-100 dark:border-slate-800 pb-4">
          <div>
            <h1 className="font-display text-xl font-bold" data-testid="invoice-form-title">New Sales Invoice</h1>
            <p className="text-xs text-slate-500 mt-0.5">{active?.name} · GSTIN {active?.gstin || "—"}</p>
          </div>
          <Select value={invoiceType} onValueChange={setInvoiceType}>
            <SelectTrigger data-testid="invoice-type" className="w-44"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="material">Material Invoice</SelectItem>
              <SelectItem value="labour">Labour Invoice</SelectItem>
              <SelectItem value="composite">Composite Invoice</SelectItem>
            </SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="sm:col-span-2">
            <Label>Customer * <Tip text="Pick from the party master. GSTIN, state and billing address auto-fill." /></Label>
            <Select value={partyId} onValueChange={setPartyId}>
              <SelectTrigger data-testid="invoice-customer" className="mt-1.5"><SelectValue placeholder="Select customer" /></SelectTrigger>
              <SelectContent>
                {parties.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.code})</SelectItem>)}
              </SelectContent>
            </Select>
            {selectedParty && (
              <p className="mt-1.5 text-xs text-slate-500">
                {selectedParty.gstin ? `GSTIN ${selectedParty.gstin} · ` : ""}{[selectedParty.billing_address, selectedParty.city, selectedParty.state].filter(Boolean).join(", ")}
              </p>
            )}
          </div>
          <div><Label>Invoice Date</Label><Input data-testid="invoice-date" type="date" className="mt-1.5" value={date} onChange={(e) => setDate(e.target.value)} /></div>
          <div><Label>Due Date</Label><Input data-testid="invoice-due-date" type="date" className="mt-1.5" value={dueDate} onChange={(e) => setDueDate(e.target.value)} /></div>
          <div className="sm:col-span-2">
            <Label>Place of Supply <Tip text="Decides the tax type: same state as your company → CGST + SGST, different state → IGST." /></Label>
            <Input data-testid="invoice-place-of-supply" className="mt-1.5" value={placeOfSupply} onChange={(e) => setPlaceOfSupply(e.target.value)} placeholder={active?.state || "State"} />
            <p className="mt-1 text-xs">
              <span className={`rounded px-1.5 py-0.5 font-mono text-[10px] font-semibold ${intra ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/15 dark:text-emerald-400" : "bg-blue-100 text-blue-700 dark:bg-blue-500/15 dark:text-blue-400"}`}
                data-testid="tax-mode-badge">{intra ? "INTRA-STATE · CGST + SGST" : "INTER-STATE · IGST"}</span>
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <AddressFields title="Billing Address" value={billingAddress} onChange={setBillingAddress} icon="billing" copyLabel="Use Customer Address" onCopy={() => selectedParty && setBillingAddress(addressFromParty(selectedParty, "billing"))} />
          <AddressFields title="Shipping Address" value={shippingAddress} onChange={setShippingAddress} icon="shipping" copyLabel="Use Billing Address" onCopy={() => setShippingAddress({ ...billingAddress })} />
        </div>

        <div>
          <div className="flex items-center justify-between mb-2">
            <Label className="text-sm font-semibold">Line Items</Label>
            <Button size="sm" variant="outline" data-testid="add-line-btn" onClick={() => setLines((ls) => [...ls, newLine()])}>
              <Plus className="mr-1 h-3.5 w-3.5" /> Add Line
            </Button>
          </div>
          <div className="overflow-x-auto rounded-lg border border-slate-200 dark:border-slate-800">
            <table className="w-full text-sm min-w-[820px]" data-testid="invoice-lines">
              <thead><tr className="bg-slate-50 dark:bg-slate-800/60 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                <th className="p-2.5 w-64">Item</th><th className="p-2.5">HSN</th><th className="p-2.5 w-20">Qty</th>
                <th className="p-2.5 w-28">Rate ₹</th><th className="p-2.5 w-20">Disc %</th><th className="p-2.5 w-24">GST %</th>
                <th className="p-2.5 text-right w-28">Amount</th><th className="p-2.5 w-10"></th></tr></thead>
              <tbody>
                {lines.map((l, i) => {
                  const base = (+l.qty || 0) * (+l.rate || 0);
                  const t = base - base * (+l.discount_pct || 0) / 100;
                  const amt = t + t * (+l.tax_rate || 0) / 100;
                  return (
                    <tr key={i} className="border-b border-slate-100 dark:border-slate-800 align-top">
                      <td className="p-2">
                        <Select value={l.item_id || (l.name ? "custom" : "")} onValueChange={(v) => pickItem(i, v)}>
                          <SelectTrigger data-testid={`line-item-${i}`} className="h-9"><SelectValue placeholder="Select item" /></SelectTrigger>
                          <SelectContent>
                            {items.map((it) => <SelectItem key={it.id} value={it.id}>{it.name}</SelectItem>)}
                            <SelectItem value="custom">— Custom line —</SelectItem>
                          </SelectContent>
                        </Select>
                        {(!l.item_id) && (
                          <Input data-testid={`line-name-${i}`} className="mt-1.5 h-8 text-xs" placeholder="Item / description"
                            value={l.name} onChange={(e) => setLine(i, "name", e.target.value)} />
                        )}
                      </td>
                      <td className="p-2"><Input className="h-9 w-20 font-mono text-xs" value={l.hsn} onChange={(e) => setLine(i, "hsn", e.target.value)} /></td>
                      <td className="p-2"><Input data-testid={`line-qty-${i}`} type="number" min="0" className="h-9" value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} /></td>
                      <td className="p-2"><Input data-testid={`line-rate-${i}`} type="number" min="0" className="h-9" value={l.rate} onChange={(e) => setLine(i, "rate", e.target.value)} /></td>
                      <td className="p-2"><Input type="number" min="0" max="100" className="h-9" value={l.discount_pct} onChange={(e) => setLine(i, "discount_pct", e.target.value)} /></td>
                      <td className="p-2">
                        <Select value={String(l.tax_rate)} onValueChange={(v) => setLine(i, "tax_rate", +v)}>
                          <SelectTrigger data-testid={`line-tax-${i}`} className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>{[0, 5, 12, 18, 28].map((r) => <SelectItem key={r} value={String(r)}>{r}%</SelectItem>)}</SelectContent>
                        </Select>
                      </td>
                      <td className="p-2 text-right font-mono text-sm pt-3">{inr(amt)}</td>
                      <td className="p-2">
                        <Button variant="ghost" size="icon" className="h-9 w-9 text-slate-400 hover:text-red-500"
                          data-testid={`line-remove-${i}`} onClick={() => setLines((ls) => ls.filter((_, j) => j !== i))} disabled={lines.length === 1}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <Label>Additional Charges (₹) <Tip text="Freight, packing, loading etc. Added to the invoice total without GST." /></Label>
              <Input data-testid="invoice-charges" type="number" min="0" className="mt-1.5" value={charges} onChange={(e) => setCharges(e.target.value)} />
            </div>
            <div>
              <Label>Amount Received Now (₹) <Tip text="Recorded as a payment voucher allocated to this invoice when posted." /></Label>
              <Input data-testid="invoice-received" type="number" min="0" className="mt-1.5" value={received} onChange={(e) => setReceived(e.target.value)} />
            </div>
            {+received > 0 && (
              <div>
                <Label>Payment Mode</Label>
                <Select value={payMode} onValueChange={setPayMode}>
                  <SelectTrigger data-testid="invoice-pay-mode" className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>{["cash", "bank_transfer", "upi", "cheque", "card", "neft", "rtgs", "imps", "other"].map((m) =>
                    <SelectItem key={m} value={m} className="capitalize">{m.replace(/_/g, " ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>Notes</Label>
              <Textarea data-testid="invoice-notes" className="mt-1.5" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Terms, delivery notes..." />
            </div>
          </div>
          <div className="rounded-xl bg-slate-50 dark:bg-slate-800/50 p-5 space-y-2 text-sm h-fit" data-testid="invoice-totals">
            {[
              ["Subtotal", calc.subtotal], [`Discount`, -calc.disc], ["Taxable Amount", calc.taxable],
              ...(intra ? [["CGST", calc.cgst], ["SGST", calc.sgst]] : [["IGST", calc.igst]]),
              ["Additional Charges", +charges || 0], ["Round Off", calc.roundOff],
            ].map(([k, v]) => (
              <div key={k} className="flex justify-between text-slate-600 dark:text-slate-300">
                <span>{k}</span><span className="font-mono">{inr(v)}</span>
              </div>
            ))}
            <div className="border-t border-slate-200 dark:border-slate-700 pt-2 flex justify-between items-center">
              <span className="font-display font-bold">Grand Total</span>
              <span className="font-mono text-xl font-bold" data-testid="invoice-grand-total">{inr(calc.total)}</span>
            </div>
            <div className="flex justify-between text-slate-600 dark:text-slate-300">
              <span>Balance Due</span>
              <span className="font-mono font-semibold text-red-600 dark:text-red-400" data-testid="invoice-balance">{inr(calc.total - (+received || 0))}</span>
            </div>
          </div>
        </div>

        <div className="flex flex-wrap justify-end gap-2 border-t border-slate-100 dark:border-slate-800 pt-4">
          <Button variant="outline" data-testid="save-draft-btn" disabled={busy} onClick={() => save("draft")}>
            <Save className="mr-1.5 h-4 w-4" /> Save Draft
          </Button>
          <Button data-testid="post-invoice-btn" disabled={busy} onClick={() => save("posted")}
            className="bg-slate-900 hover:bg-slate-800 dark:bg-slate-100 dark:text-slate-900">
            <Send className="mr-1.5 h-4 w-4" /> {busy ? "Posting..." : "Post Invoice"}
          </Button>
        </div>
      </div>
    </div>
  );
}
